import { useFavicon } from '@vueuse/core'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { type Ref, ref } from 'vue'

import { Application } from '@/models'
import type { Settings } from '@/models/Setting'
import { FetchError } from '@/plugins/http'
import { type Locale, setI18nLocale } from '@/plugins/i18n'
import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'
import { useIdleStore } from '@/stores/idle'

export const useSettingsStore = defineStore(
  'settings',
  () => {
    const settings: Ref<Partial<Settings>> = ref({})
    const hasFetched: Ref<boolean> = ref(false)
    const locale: Ref<LocaleSetting> = ref('nl')
    const defaultOfficeId: Ref<string | undefined> = ref(undefined)

    function setDefaultOfficeId(id: string | undefined): void {
      defaultOfficeId.value = id
    }

    function setHasFetched(value: boolean = true) {
      hasFetched.value = value
    }

    function setSettings(value: Partial<Settings> | undefined) {
      settings.value = { ...settings.value, ...value }
    }

    function setLocale(value?: LocaleSetting | undefined) {
      if (value) locale.value = value

      setI18nLocale(locale.value)
    }

    function get<T extends keyof Settings>(key: T): Settings[T] | undefined {
      if (!settings.value) return

      // eslint-disable-next-line security/detect-object-injection
      return settings.value[key]
    }

    async function refresh(): Promise<void> {
      const auth = useAuthStore()
      const idle = useIdleStore()

      if (!auth.isAuthenticated) return

      try {
        setSettings(await Application.settings())
      } catch (error) {
        // Tenant settings are admin-only; everyone else keeps the defaults instead of failing navigation.
        if (!(error instanceof FetchError) || error.status !== 403) throw error

        setHasFetched()
        return
      }

      idle.threshold = (get('idle.seconds') || 0) * 1000 // Convert seconds to milliseconds

      setHasFetched()
    }

    async function teapot(): Promise<void> {
      const app = useAppStore()

      setSettings(await Application.config())

      app.setTitle(get('title'))

      if (get('favicon')) {
        useFavicon(get('favicon'))
      }
    }

    return { get, hasFetched, refresh, settings, setHasFetched, teapot, setSettings, locale, setLocale, defaultOfficeId, setDefaultOfficeId }
  },
  {
    persist: {
      pick: ['locale', 'defaultOfficeId'],
      storage: localStorage,
      afterHydrate(context) {
        context.store.setLocale(context.store.$state.locale)
      },
    },
  },
)

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSettingsStore, import.meta.hot))
}

export type ThemeSetting = 'light' | 'dark'
export type LocaleSetting = Locale
